/**
 * Platform Demo Requests inbox — track demo enquiries from the landing page.
 */
import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageContainer, PageHeader, EmptyState } from "@/components/app/PageShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Headset, Trash, EnvelopeSimple, Phone, Buildings, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_COLORS = {
    new: "bg-yellow-500", contacted: "bg-primary", qualified: "bg-success", closed: "bg-muted-foreground",
};

export default function PlatformDemoRequestsPage() {
    const [requests, setRequests] = useState([]);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        try { const { data } = await api.get("/platform/demo-requests"); setRequests(data); }
        catch (e) { toast.error(extractError(e)); }
    };
    useEffect(() => { load(); }, []);

    const updateStatus = async (id, status) => {
        try { await api.patch(`/platform/demo-requests/${id}`, { status }); await load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    const saveNotes = async () => {
        try {
            await api.patch(`/platform/demo-requests/${editing.id}`, { notes: editing.notes, status: editing.status });
            toast.success("Saved"); setEditing(null); await load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const remove = async (r) => {
        if (!confirm(`Delete demo request from ${r.name}?`)) return;
        try { await api.delete(`/platform/demo-requests/${r.id}`); await load(); }
        catch (e) { toast.error(extractError(e)); }
    };

    return (
        <PageContainer>
            <PageHeader
                overline="Platform"
                title="Demo requests"
                description="Inquiries submitted via the landing-page demo form."
                testid="demo-requests-header"
            />

            {requests.length === 0 ? (
                <EmptyState icon={Headset} title="No demo requests yet" description="When prospects submit the demo form, they'll show up here." testid="demo-empty" />
            ) : (
                <div className="space-y-3" data-testid="demo-requests-list">
                    {requests.map((r) => (
                        <div key={r.id} className="brut-card p-5" data-testid={`demo-card-${r.id}`}>
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-lg font-semibold tracking-tight truncate">{r.name}</h3>
                                        <Badge className={`rounded-sm font-mono text-[10px] ${STATUS_COLORS[r.status] || ""}`}>{r.status}</Badge>
                                    </div>
                                    <div className="grid sm:grid-cols-3 gap-3 text-xs text-muted-foreground mb-3">
                                        <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-primary truncate" data-testid={`demo-email-${r.id}`}>
                                            <EnvelopeSimple size={12} /> <span className="truncate">{r.email}</span>
                                        </a>
                                        {r.phone && (
                                            <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 hover:text-primary" data-testid={`demo-phone-${r.id}`}>
                                                <Phone size={12} /> {r.phone}
                                            </a>
                                        )}
                                        {r.company && (
                                            <span className="flex items-center gap-1.5">
                                                <Buildings size={12} /> {r.company}
                                            </span>
                                        )}
                                    </div>
                                    {r.message && <p className="text-sm text-foreground/80 leading-relaxed mb-3 whitespace-pre-line">{r.message}</p>}
                                    {r.notes && <div className="text-xs text-muted-foreground border-l-2 border-primary/40 pl-3 italic mb-2"><strong>Notes:</strong> {r.notes}</div>}
                                    <div className="text-[10px] font-mono text-muted-foreground">
                                        Submitted {new Date(r.created_at).toLocaleString()}{r.ip ? ` · ${r.ip}` : ""}
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 items-end">
                                    <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                                        <SelectTrigger className="rounded-sm w-32 h-8 text-xs" data-testid={`demo-status-${r.id}`}><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="new">New</SelectItem>
                                            <SelectItem value="contacted">Contacted</SelectItem>
                                            <SelectItem value="qualified">Qualified</SelectItem>
                                            <SelectItem value="closed">Closed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Button variant="outline" size="sm" className="rounded-sm h-8 text-xs" onClick={() => setEditing({ ...r })} data-testid={`demo-edit-${r.id}`}>
                                        <ArrowRight size={12} className="mr-1" /> Add notes
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded-sm text-destructive" onClick={() => remove(r)} data-testid={`demo-del-${r.id}`}><Trash size={12} /></Button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
                <DialogContent className="rounded-sm" data-testid="demo-notes-dialog">
                    {editing && (
                        <>
                            <DialogHeader>
                                <DialogTitle>{editing.name} · {editing.email}</DialogTitle>
                            </DialogHeader>
                            <div className="space-y-3">
                                <Textarea
                                    rows={5}
                                    placeholder="Add follow-up notes…"
                                    value={editing.notes}
                                    onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                                    className="rounded-sm"
                                    data-testid="demo-notes-input"
                                />
                            </div>
                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setEditing(null)} className="rounded-sm">Cancel</Button>
                                <Button onClick={saveNotes} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90" data-testid="demo-notes-save">Save</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
